import {
  Meta,
  Links,
  Outlet,
  Scripts,
  useLoaderData,
  useLocation,
} from "@remix-run/react";
import { setUser, withSentry } from "@sentry/remix";
import { json } from "@remix-run/node";
import {
  getSupabaseEnv,
  getSupabaseWithSessionHeaders,
} from "./lib/supabase_server";
import { serverPublicOrigin } from "./lib/serverPublicOrigin";
import type { LinksFunction, LoaderFunctionArgs } from "@remix-run/node";
import { SupabaseContext } from "./lib/supabase_context";
import type { Session } from "@supabase/supabase-js";
import { CartProvider } from "./context/cartContext";
import { FavoriteProvider } from "./context/favoriteContext";
import { useSupabase } from "./lib/supabase";
import { useEffect } from "react";
import { Toaster } from "./components/ui/toaster";
import { CartPopupWrapper } from "./components/CartPopupWrapper";
import { FeedbackButton } from "./components/FeedbackButton";
import stylesheet from "./tailwind.css?url";

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: stylesheet },
];

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const { session, headers } = await getSupabaseWithSessionHeaders({
    request,
  });
  /** Attach identity to this request's Sentry scope for server-side captures. */
  setUser(
    session?.user
      ? { id: session.user.id, email: session.user.email ?? undefined }
      : null,
  );
  const publicOrigin = serverPublicOrigin(request);
  return json(
    {
      env: getSupabaseEnv(),
      session,
      /** Canonical app URL for auth `emailRedirectTo`, etc. (avoid empty client-only state). */
      publicOrigin,
    },
    { headers },
  );
};
export { ErrorBoundary } from "./components/RootErrorBoundary";

function App() {
  const { env, session, publicOrigin } = useLoaderData<typeof loader>();
  const { supabase } = useSupabase({ env, session });
  const location = useLocation();

  const userId = session?.user?.id ?? null;
  const userEmail = session?.user?.email ?? null;
  useEffect(() => {
    setUser(userId ? { id: userId, email: userEmail ?? undefined } : null);
  }, [userId, userEmail]);

  // Scroll to top on navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <SupabaseContext.Provider
      value={{
        supabase,
        domainUrl: publicOrigin,
        session: session as Session,
        googleApiKey: env.GOOGLE_API_KEY,
        turnstileSiteKey: env.TURNSTILE_SITE_KEY,
      }}
    >
      <html lang="en">
        <head>
          <Links />
          <Meta />
          <meta charSet="utf-8" />
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, viewport-fit=cover"
          />

          <meta
            name="description"
            content="Source international, ethnic, and specialty food products from verified wholesalers and distributors. BulkBuyPlace helps retailers, restaurants, and small businesses buy in bulk at competitive wholesale prices."
          />
          <meta name="theme-color" content="#10b981" />
          <meta name="twitter:card" content="summary_large_image" />
          {/* TODO: add a real OG image at /public/og-image.png (recommended 1200x630) and reference it here. */}
        </head>
        <body>
          <CartProvider>
            <FavoriteProvider>
              <Outlet />
            </FavoriteProvider>
            <CartPopupWrapper />
            <FeedbackButton />
          </CartProvider>
          <Scripts />
          <Toaster />
        </body>
      </html>
    </SupabaseContext.Provider>
  );
}

export default withSentry(App, { wrapWithErrorBoundary: false });
